/**
 * lib/roleAuthority.ts
 * ------------------------------------------------------------------
 * Delegated user-management: the EXPLICIT map of which roles each
 * actor may assign. This is the escalation guard, stated as data
 * rather than fragile priority math.
 *
 * SECURITY: this module is the UI-side mirror. The authoritative
 * enforcement lives server-side in the setUserRole Cloud Function,
 * which carries an identical map. The UI uses this to hide roles an
 * actor may not assign; the function REJECTS forbidden assignments
 * regardless of what the client sends. Never rely on the UI alone.
 *
 * RULES ENCODED
 *  - admin (Proprietor): may assign every role, including admin and
 *    systemAdmin (the only same-tier self-replication permitted).
 *  - systemAdmin: technical/audit/backups only — assigns NOTHING.
 *  - academicAdmin: only academic worker roles.
 *  - superAcademicAdmin: academic worker roles, exams and admissions. NEVER
 *    an HR or finance role, and never an admin tier — the school asked for
 *    exactly that boundary, and it is the whole point of this map.
 *  - operationsAdmin: only admissions/HR/finance worker roles.
 *  - everyone else: assigns nothing.
 * ------------------------------------------------------------------
 */
import type { Role } from '@/types';

/** Roles each actor-role is permitted to assign to other users. */
export const ASSIGNABLE_ROLES: Partial<Record<Role, Role[]>> = {
  admin: [
    // Proprietor: everything. Listed explicitly (no wildcard) so the
    // set is auditable and adding a new role forces a conscious choice.
    'admin',
    'systemAdmin',
    'academicAdmin',
    'superAcademicAdmin',
    'operationsAdmin',
    'headteacher',
    'editor',
    'hoy',
    'hod',
    'classTeacher',
    'subjectTeacher',
    'admissions',
    'hrAdmin',
    'hrOfficer',
    'payrollOfficer',
    'financeAdmin',
    'feesOfficer',
    'examsOfficer',
    'bursar_assistant',
    'hr',
    'bursar',
    'staff',
    'parent',
    'media',
  ],
  // systemAdmin assigns nothing (technical operator only).
  systemAdmin: [],
  academicAdmin: ['editor', 'hoy', 'hod', 'classTeacher', 'subjectTeacher', 'examsOfficer'],
  /* Academic, exams and admissions. No hrAdmin/hrOfficer/payrollOfficer, no
     financeAdmin/feesOfficer/bursar/bursar_assistant, and no admin tier —
     not even their own: this role sets up teachers, not the people who
     touch pay or fees, and not more people like themselves. */
  superAcademicAdmin: [
    'editor', 'hoy', 'hod', 'classTeacher', 'subjectTeacher', 'examsOfficer', 'admissions',
  ],
  operationsAdmin: [
    'admissions',
    'hrOfficer',
    'payrollOfficer',
    'feesOfficer',
    'examsOfficer',
    'bursar_assistant',
  ],
};

/** May an actor holding `actorRoles` assign the single role `target`? */
export function canAssignRole(
  actorRoles: Role[] | null | undefined,
  target: Role,
): boolean {
  if (!actorRoles || actorRoles.length === 0) return false;
  // Union across all of the actor's roles (a multi-role actor may
  // assign anything any of their roles permits).
  for (const ar of actorRoles) {
    const allowed = ASSIGNABLE_ROLES[ar];
    if (allowed && allowed.includes(target)) return true;
  }
  return false;
}

/** The full set of roles an actor may assign (union across their roles). */
export function assignableRolesFor(
  actorRoles: Role[] | null | undefined,
): Role[] {
  if (!actorRoles || actorRoles.length === 0) return [];
  const out = new Set<Role>();
  for (const ar of actorRoles) {
    (ASSIGNABLE_ROLES[ar] ?? []).forEach((r) => out.add(r));
  }
  return [...out];
}

/** May this actor assign EVERY role in the requested set? (all-or-nothing) */
export function canAssignAll(
  actorRoles: Role[] | null | undefined,
  targets: Role[],
): boolean {
  return targets.every((t) => canAssignRole(actorRoles, t));
}

/**
 * Which users (by their roles) may this actor MANAGE (view/edit in the
 * delegated user-management screen)? A user is manageable if every one
 * of their current roles is assignable by the actor — i.e. the actor
 * could have created them. This is what scopes each domain admin to
 * "only their own" users.
 */
export function canManageUserWithRoles(
  actorRoles: Role[] | null | undefined,
  targetUserRoles: Role[] | null | undefined,
): boolean {
  if (!targetUserRoles || targetUserRoles.length === 0) return false;
  return targetUserRoles.every((r) => canAssignRole(actorRoles, r));
}
